import OrderFinancialBreakdown from 'src/components/shared/OrderFinancialBreakdown'
import { Button, Table, message, Input, InputNumber } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import axios from 'axios'
import OMSLogo from 'src/assets/images/OMSLogoWht.png'
import { H1, H3, H4, BodyText } from 'src/branding/typography'
import LoadingScreen from 'src/components/shared/LoadingScreen'
import FadeIn from 'src/components/styled/FadeIn'
import config from 'src/config'

// Determine Express URL based on environment
const getExpressUrl = () => {
  // Check if environment variable is set
  if (
    config.apiGateway.EXPRESS_URL &&
    config.apiGateway.EXPRESS_URL !== 'http://localhost:8000'
  ) {
    // Remove /api/v1 if it's included in the URL (CloudFront URLs might include it)
    let url = config.apiGateway.EXPRESS_URL
    if (url.endsWith('/api/v1')) {
      url = url.replace(/\/api\/v1$/, '')
    }
    return url
  }

  // Detect environment from window location (only if window is available)
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname
    if (hostname.includes('oms-dash-staging') || hostname.includes('staging')) {
      // Staging Express URL (CloudFront - no /api/v1 in base URL)
      return 'https://d8487rleyyaj5.cloudfront.net'
    } else if (
      hostname.includes('dashboard.ordersmadesimple.com') ||
      hostname.includes('production')
    ) {
      // Production Express URL (CloudFront - no /api/v1 in base URL)
      return 'https://dtnkohlhk5qr6.cloudfront.net'
    }
  }

  // Development - use localhost or env var
  return config.apiGateway.EXPRESS_URL || 'http://localhost:8000'
}

// Create public API instance - will be configured per-request with correct baseURL
const createPublicAPI = baseURL => {
  return axios.create({
    baseURL,
    timeout: 30000, // 30 second timeout
  })
}

// Styled components matching OrderChecklist exactly
const Wrapper = styled(FadeIn)`
  background-color: ${({ theme }) => theme.colours.mayoWhite};
`

const Inner = styled.div`
  width: 60%;
  margin: 0 auto;
  ${({ theme }) => theme.device('sm')`
      padding: 0 15px;
  `}
`

const Header = styled.header`
  background: ${({ theme }) => theme.colours.bbqBlack};
  padding: ${({ theme }) => `${theme.spacing.xl} 0`};
`

const VenueName = styled(H1)`
  line-height: ${({ theme }) => theme.spacing.xl};
  padding: 10px 0;
  color: ${({ theme }) => theme.colours.lettuceGreen};
  margin-bottom: 0;
`

const HeaderContent = styled(Inner)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LeftHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  color: ${({ theme }) => theme.colours.mayoWhite};
`

const Logo = styled.img`
  height: 108px;
`

const OrderDetails = styled(Inner)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: left;
  margin-top: 50px;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 5px;
  padding: ${({ theme }) => theme.spacing.sm} !important;
`

const OrderDetailsHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  h3 {
    margin: 0 0 5px 0;
  }
`

const ImportantLabel = styled.div`
  padding: 0 5px;
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colours.chilliRed};
  color: ${({ theme }) => theme.colours.chilliRed};
  border-radius: 5px;
  height: 30px;
  span {
    margin: 0 5px;
  }
`

const OrdersDetailsText = styled(H4)`
  color: ${({ theme }) => theme.colours.chilliRed};
  margin: 10px 0 5px 0;
`

const OrderDetailsNotes = styled(BodyText)`
  font-weight: 700;
  width: fit-content;
`

const InnerBody = styled(Inner)`
  .ant-table-thead * {
    background-color: ${({ theme }) => theme.colours.mayoWhite};
    white-space: nowrap;
  }

  .approval-qty-changed td {
    background-color: #fffbe6;
  }
`

const TableHeader = styled.div`
  margin: ${({ theme }) => `${theme.spacing.xl} 0 ${theme.spacing.lg} 0`};
`

const OrderNumber = styled.div`
  font-family: 'Larsseit';
  font-style: normal;
  font-weight: bold;
  font-size: 1rem;
`

const RowTotalPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${({ total, theme }) =>
    total ? `${theme.spacing.lg} 0 0 0` : `${theme.spacing.lg} 0`};
`

const Footer = styled.footer`
  background-color: ${({ theme }) => theme.colours.bbqBlack};
  color: ${({ theme }) => theme.colours.mayoWhite};
  padding: ${({ theme }) => `${theme.spacing.sm} 0`};
  text-align: center;
`

const AddressRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const LeftColumn = styled.div`
  p {
    display: flex;
    flex-direction: column;
  }
  H4 {
    text-align: left;
    color: white;
  }
`

const RowButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin: ${({ theme }) => `${theme.spacing.lg} 0 ${theme.spacing.xl} 0`};
`

const Buttons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  width: 100%;
`

const ApproveButton = styled(Button)`
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  border-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: white;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  padding: 0 40px;

  &:hover {
    background-color: ${({ theme }) => theme.colours.lettuceGreen};
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
    opacity: 0.9;
    color: white;
  }
`

const DeclineButton = styled(Button)`
  background-color: ${({ theme }) => theme.colours.chilliRed};
  border-color: ${({ theme }) => theme.colours.chilliRed};
  color: white;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  padding: 0 40px;

  &:hover {
    background-color: ${({ theme }) => theme.colours.chilliRed};
    border-color: ${({ theme }) => theme.colours.chilliRed};
    opacity: 0.9;
    color: white;
  }
`

const SuccessContainer = styled(Inner)`
  text-align: center;
  padding: 100px 20px;
`

const SuccessIcon = styled.div`
  font-size: 64px;
  color: ${props => (props.success ? '#52c41a' : '#dc3545')};
  margin-bottom: 20px;
`

const { TextArea } = Input

const recalculateApprovalTotals = items => {
  let subtotal = 0
  let vatTotal = 0

  for (const item of items) {
    const quantity = Number(item.quantity) || 0
    const unitPrice = Number(item.unitPrice) || 0
    const lineTotal = quantity * unitPrice
    const lineVat = item.hasVat ? lineTotal * 0.2 : 0
    subtotal += lineTotal
    vatTotal += lineVat
  }

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    vat: parseFloat(vatTotal.toFixed(2)),
    total: parseFloat((subtotal + vatTotal).toFixed(2)),
  }
}

const OrderApprovalScreen = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [orderData, setOrderData] = useState(null)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null) // 'approved' or 'declined'
  const [renderError, setRenderError] = useState(null)
  const [managerMessage, setManagerMessage] = useState('')
  // Editable quantities keyed by orderId (manager amend-before-approve)
  const [editedQuantities, setEditedQuantities] = useState({})

  useEffect(() => {
    try {
      // Component initialization
    } catch (err) {
      console.error('OrderApprovalScreen: Error in mount effect:', err)
      setRenderError(
        'Failed to initialize component: ' + (err?.message || 'Unknown error'),
      )
    }
  }, [token])

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const expressUrl = getExpressUrl()
      // Ensure baseURL doesn't include /api/v1 (strip it if present)
      const cleanBaseUrl = expressUrl.replace(/\/api\/v1\/?$/, '')
      const publicAPI = createPublicAPI(cleanBaseUrl)
      // Always use /api/v1/order-approval since baseURL should NOT include /api/v1
      const apiPath = `/api/v1/order-approval/${token}`

      const response = await publicAPI.get(apiPath)

      if (response.data.success) {
        const data = response.data.data
        setOrderData(data)

        const initialQuantities = {}
        ;(data.items || []).forEach(item => {
          const id = item.orderId != null ? String(item.orderId) : null
          if (id) {
            initialQuantities[id] = Number(item.quantity) || 0
          }
        })
        setEditedQuantities(initialQuantities)

        // If order is already processed, set result state to show proper message
        if (data.isAlreadyProcessed && data.approvalStatus) {
          if (data.approvalStatus === 'approved') {
            setResult('approved')
          } else if (
            data.approvalStatus === 'rejected' ||
            data.approvalStatus === 'auto_rejected'
          ) {
            setResult('declined')
          }
        }
      } else {
        setError(response.data.message || 'Failed to load order details')
      }
    } catch (err) {
      console.error('OrderApprovalScreen: Error fetching order details:', err)
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to load order details. The link may be invalid or expired.',
      )
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (!token) {
      setError('Invalid approval link: missing token')
      setLoading(false)
      return
    }
    fetchOrderDetails()
  }, [fetchOrderDetails, token])

  const editableItems = useMemo(() => {
    if (!orderData?.items) return []

    return orderData.items.map(item => {
      const orderId = item.orderId != null ? String(item.orderId) : null
      const originalQuantity =
        item.originalQuantity != null
          ? Number(item.originalQuantity)
          : Number(item.quantity) || 0
      const quantity =
        orderId && editedQuantities[orderId] != null
          ? Number(editedQuantities[orderId])
          : Number(item.quantity) || 0
      const unitPrice = Number(item.unitPrice) || 0
      const total = quantity * unitPrice

      return {
        ...item,
        orderId,
        originalQuantity,
        quantity,
        total,
        isChanged: quantity !== originalQuantity,
        isRemoved: quantity === 0,
      }
    })
  }, [orderData, editedQuantities])

  const liveTotals = useMemo(
    () => recalculateApprovalTotals(editableItems),
    [editableItems],
  )

  const hasQuantityChanges = useMemo(
    () => editableItems.some(item => item.isChanged),
    [editableItems],
  )

  const remainingItemCount = useMemo(
    () => editableItems.filter(item => item.quantity > 0).length,
    [editableItems],
  )

  const handleQuantityChange = (orderId, value) => {
    if (!orderId) return
    // null/empty while typing → treat as 0 for safety on approve; allow clear mid-edit
    const next =
      value == null || value === '' ? 0 : Math.max(0, Number(value) || 0)
    setEditedQuantities(prev => ({
      ...prev,
      [orderId]: next,
    }))
  }

  const handleRemoveLine = orderId => {
    handleQuantityChange(orderId, 0)
  }

  const handleApprove = async () => {
    try {
      if (managerMessage.length > 1000) {
        message.error('Message to requester must be 1000 characters or fewer.')
        return
      }

      if (remainingItemCount === 0) {
        message.error(
          'Cannot approve an order with no remaining products. Reject the order instead.',
        )
        return
      }

      const missingOrderId = editableItems.some(item => !item.orderId)
      if (missingOrderId) {
        message.error(
          'Unable to amend quantities for this order. Please refresh and try again.',
        )
        return
      }

      setProcessing(true)
      const expressUrl = getExpressUrl()
      const cleanBaseUrl = expressUrl.replace(/\/api\/v1\/?$/, '')
      const publicAPI = createPublicAPI(cleanBaseUrl)
      const apiPath = `/api/v1/order-approval/${token}/approve`
      const response = await publicAPI.post(apiPath, {
        action: 'approve',
        message: managerMessage,
        items: editableItems.map(item => ({
          orderId: item.orderId,
          quantity: item.quantity,
        })),
      })

      if (response.data.success) {
        setResult('approved')
        message.success(
          response.data.data?.hasQuantityChanges
            ? 'Order approved with adjustments!'
            : 'Order approved successfully!',
        )
      } else {
        message.error(response.data.message || 'Failed to approve order')
      }
    } catch (err) {
      console.error('Error approving order:', err)
      message.error(
        err.response?.data?.message ||
          'Failed to approve order. Please try again.',
      )
    } finally {
      setProcessing(false)
    }
  }

  const handleDecline = async () => {
    try {
      if (managerMessage.length > 1000) {
        message.error('Message to requester must be 1000 characters or fewer.')
        return
      }

      setProcessing(true)
      const expressUrl = getExpressUrl()
      const cleanBaseUrl = expressUrl.replace(/\/api\/v1\/?$/, '')
      const publicAPI = createPublicAPI(cleanBaseUrl)
      const apiPath = `/api/v1/order-approval/${token}/decline`
      const response = await publicAPI.post(apiPath, {
        action: 'decline',
        message: managerMessage,
      })

      if (response.data.success) {
        setResult('declined')
        message.success('Order declined successfully')
      } else {
        message.error(response.data.message || 'Failed to decline order')
      }
    } catch (err) {
      console.error('Error declining order:', err)
      message.error(
        err.response?.data?.message ||
          'Failed to decline order. Please try again.',
      )
    } finally {
      setProcessing(false)
    }
  }

  // Always render something - even if there's an error
  // This ensures the component never returns undefined/null

  // Handle render errors
  if (renderError) {
    return (
      <Wrapper>
        <SuccessContainer>
          <SuccessIcon success={false}>✗</SuccessIcon>
          <H3>Error Loading Page</H3>
          <BodyText style={{ marginTop: '20px', color: '#666' }}>
            {renderError}
          </BodyText>
          <BodyText
            style={{ marginTop: '10px', color: '#999', fontSize: '12px' }}
          >
            Please check the browser console for more details.
          </BodyText>
        </SuccessContainer>
      </Wrapper>
    )
  }

  const columns = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: 'Product',
      dataIndex: 'productTitle',
      key: 'productTitle',
      render: (title, record) => (
        <span
          style={
            record.isRemoved
              ? { textDecoration: 'line-through', color: '#999' }
              : undefined
          }
        >
          {title}
          {record.isChanged && !record.isRemoved && (
            <span style={{ marginLeft: 8, color: '#d48806', fontSize: 12 }}>
              (was {record.originalQuantity})
            </span>
          )}
          {record.isRemoved && (
            <span style={{ marginLeft: 8, color: '#cf1322', fontSize: 12 }}>
              removed
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Original Qty',
      dataIndex: 'originalQuantity',
      key: 'originalQuantity',
      align: 'center',
    },
    {
      title: 'Approved Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      render: (quantity, record) => (
        <InputNumber
          min={0}
          value={quantity}
          disabled={processing}
          onChange={value => handleQuantityChange(record.orderId, value)}
          style={{
            width: 80,
            backgroundColor: record.isChanged ? '#fffbe6' : undefined,
          }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      render: (price, record) => {
        try {
          return `£${parseFloat(price || 0).toFixed(2)}${
            record?.hasVat ? ' + VAT' : ''
          }`
        } catch (e) {
          return 'N/A'
        }
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: total => {
        try {
          return `£${parseFloat(total || 0).toFixed(2)}`
        } catch (e) {
          return 'N/A'
        }
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: notes => notes || '-',
    },
    {
      title: '',
      key: 'remove',
      align: 'center',
      render: (_, record) => (
        <Button
          type='link'
          danger
          icon={<DeleteOutlined />}
          disabled={processing || record.quantity === 0}
          onClick={() => handleRemoveLine(record.orderId)}
        >
          Remove
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <Wrapper>
        <LoadingScreen />
      </Wrapper>
    )
  }

  if (error) {
    return (
      <Wrapper>
        <SuccessContainer>
          <SuccessIcon success={false}>✗</SuccessIcon>
          <H3>{error}</H3>
          <BodyText style={{ marginTop: '20px', color: '#666' }}>
            This link may be invalid, expired, or the order may have already
            been processed.
          </BodyText>
        </SuccessContainer>
      </Wrapper>
    )
  }

  if (result) {
    return (
      <Wrapper>
        <Header>
          <HeaderContent>
            <LeftHeader>
              <BodyText>
                {result === 'approved' ? 'Order Approved' : 'Order Declined'}
              </BodyText>
              {orderData && (
                <>
                  <VenueName>{orderData.venue.title}</VenueName>
                  <BodyText>Supplier: {orderData.supplier.title}</BodyText>
                </>
              )}
            </LeftHeader>
            <Logo src={OMSLogo} alt='Orders Made Simple logo' />
          </HeaderContent>
        </Header>
        <SuccessContainer>
          <SuccessIcon success={result === 'approved'}>
            {result === 'approved' ? (
              <CheckCircleOutlined style={{ fontSize: '64px' }} />
            ) : (
              <CloseCircleOutlined style={{ fontSize: '64px' }} />
            )}
          </SuccessIcon>
          <H1>{result === 'approved' ? 'Order Approved' : 'Order Declined'}</H1>
          <BodyText style={{ marginTop: '20px', fontSize: '16px' }}>
            {result === 'approved'
              ? 'The order has been approved and will now be sent to the supplier.'
              : 'The order has been declined and will not be sent to the supplier.'}
          </BodyText>
        </SuccessContainer>
      </Wrapper>
    )
  }

  if (!orderData) {
    return (
      <Wrapper>
        <SuccessContainer>
          <H3>Order not found</H3>
          <BodyText style={{ marginTop: '20px', color: '#666' }}>
            Unable to load order details.
          </BodyText>
        </SuccessContainer>
      </Wrapper>
    )
  }

  // Check if order is already processed and show appropriate message
  if (orderData.isAlreadyProcessed && orderData.approvalStatus) {
    const status = orderData.approvalStatus
    const isApproved = status === 'approved'

    return (
      <Wrapper>
        <Header>
          <HeaderContent>
            <LeftHeader>
              <BodyText>
                {isApproved ? 'Order Approved' : 'Order Declined'}
              </BodyText>
              <VenueName>{orderData.venue.title}</VenueName>
              <BodyText>Supplier: {orderData.supplier.title}</BodyText>
              {orderData.purchaseOrderNumber && (
                <BodyText>PO Number: {orderData.purchaseOrderNumber}</BodyText>
              )}
            </LeftHeader>
            <Logo src={OMSLogo} alt='Orders Made Simple logo' />
          </HeaderContent>
        </Header>
        <SuccessContainer>
          <SuccessIcon success={isApproved}>
            {isApproved ? (
              <CheckCircleOutlined style={{ fontSize: '64px' }} />
            ) : (
              <CloseCircleOutlined style={{ fontSize: '64px' }} />
            )}
          </SuccessIcon>
          <H1>{isApproved ? 'Order Approved' : 'Order Declined'}</H1>
          <BodyText style={{ marginTop: '20px', fontSize: '16px' }}>
            {isApproved
              ? 'This order has been approved and sent to the supplier.'
              : 'This order has been declined and will not be sent to the supplier.'}
          </BodyText>
        </SuccessContainer>
      </Wrapper>
    )
  }

  const requiredDeliveryDay = orderData.requiredDeliveryDay
    ? new Date(orderData.requiredDeliveryDay).toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Next available delivery day'

  return (
    <Wrapper>
      <Header>
        <HeaderContent>
          <LeftHeader>
            <BodyText>A new order requires your approval</BodyText>
            <VenueName>{orderData.venue.title}</VenueName>
            <BodyText>Supplier: {orderData.supplier.title}</BodyText>
            {orderData.purchaseOrderNumber && (
              <BodyText>PO Number: {orderData.purchaseOrderNumber}</BodyText>
            )}
          </LeftHeader>
          <Logo src={OMSLogo} alt='Orders Made Simple logo' />
        </HeaderContent>
      </Header>

      <OrderDetails>
        <OrderDetailsHeader>
          <H3>Order details</H3>
          <ImportantLabel>
            <ExclamationCircleOutlined />
            <span>Important</span>
          </ImportantLabel>
        </OrderDetailsHeader>
        <BodyText>
          Please review the order below. You can adjust quantities or remove
          products before approving, or decline the order.
        </BodyText>
        <OrdersDetailsText>Required delivery date</OrdersDetailsText>
        <OrderDetailsNotes>{requiredDeliveryDay}</OrderDetailsNotes>
        {orderData.requestedBy?.trim() && (
          <>
            <OrdersDetailsText>Requested by</OrdersDetailsText>
            <OrderDetailsNotes>
              {orderData.requestedBy.trim()}
            </OrderDetailsNotes>
          </>
        )}
        {orderData.notes && (
          <>
            <OrdersDetailsText>Order notes</OrdersDetailsText>
            <OrderDetailsNotes>{orderData.notes}</OrderDetailsNotes>
          </>
        )}
      </OrderDetails>

      <InnerBody>
        <TableHeader>
          <H4>Order items</H4>
          {orderData.purchaseOrderNumber && (
            <OrderNumber>
              Purchase order # {orderData.purchaseOrderNumber}
            </OrderNumber>
          )}
        </TableHeader>
        <Table
          columns={columns}
          dataSource={editableItems.map((item, index) => ({
            ...item,
            key: item.orderId || index,
          }))}
          pagination={false}
          rowClassName={record =>
            record.isChanged ? 'approval-qty-changed' : ''
          }
        />
      </InnerBody>

      <InnerBody>
        {hasQuantityChanges && (
          <BodyText style={{ marginBottom: 12, color: '#d48806' }}>
            You have adjusted one or more quantities. The amended order will be
            sent to the supplier.
          </BodyText>
        )}
        {remainingItemCount === 0 && (
          <BodyText style={{ marginBottom: 12, color: '#cf1322' }}>
            All products have been removed. Reject the order instead of
            approving.
          </BodyText>
        )}
        <OrderFinancialBreakdown
          conflict={orderData?.financialSummary?.hasConflict}
          orders={
            orderData?.orderFinancialSnapshot
              ? [{ orderFinancialSnapshot: orderData.orderFinancialSnapshot }]
              : []
          }
          context={{
            venueId: orderData?.venue?.id,
            supplierId: orderData?.supplier?.id,
            purchaseOrderNumber: orderData?.purchaseOrderNumber,
          }}
        />
        <RowTotalPrice>
          <BodyText>
            <b>
              {orderData?.orderFinancialSnapshot
                ? 'Goods ex VAT:'
                : 'Total ex VAT:'}
            </b>
          </BodyText>
          <BodyText>
            <b>£{liveTotals.subtotal.toFixed(2)}</b>
          </BodyText>
        </RowTotalPrice>
        <RowTotalPrice>
          <BodyText>
            <b>VAT Amount:</b>
          </BodyText>
          <BodyText>
            <b>£{liveTotals.vat.toFixed(2)}</b>
          </BodyText>
        </RowTotalPrice>
        <RowTotalPrice total>
          <BodyText>
            <b>
              {orderData?.orderFinancialSnapshot
                ? 'Goods incl VAT'
                : 'Total Amount'}
            </b>
          </BodyText>
          <BodyText>
            <b>£{liveTotals.total.toFixed(2)}</b>
          </BodyText>
        </RowTotalPrice>

        <div style={{ marginTop: 24 }}>
          <H4>Message to requester (optional)</H4>
          <BodyText style={{ marginBottom: 8, color: '#666' }}>
            Add context for the requester (e.g. Please use main supplier unless
            out of stock)
          </BodyText>
          <TextArea
            rows={4}
            value={managerMessage}
            onChange={e => setManagerMessage(e.target.value)}
            maxLength={1000}
            placeholder='Add context for the requester (e.g. Please use main supplier unless out of stock)'
          />
          <BodyText
            style={{
              marginTop: 4,
              fontSize: 12,
              textAlign: 'right',
              color:
                managerMessage.length > 1000
                  ? '#f5222d'
                  : managerMessage.length > 400
                  ? '#faad14'
                  : '#999',
            }}
          >
            {managerMessage.length}/1000 characters
          </BodyText>
        </div>

        <RowButton>
          <Buttons>
            <DeclineButton
              icon={<CloseCircleOutlined />}
              onClick={handleDecline}
              loading={processing}
              disabled={processing}
            >
              Decline Order
            </DeclineButton>
            <ApproveButton
              icon={<CheckCircleOutlined />}
              onClick={handleApprove}
              loading={processing}
              disabled={processing || remainingItemCount === 0}
            >
              {hasQuantityChanges ? 'Approve Adjusted Order' : 'Approve Order'}
            </ApproveButton>
          </Buttons>
        </RowButton>
        <BodyText
          style={{ textAlign: 'center', color: '#ff9800', marginTop: '20px' }}
        >
          ⚠️ If no action is taken within 48 hours, this order will be
          automatically rejected.
        </BodyText>
      </InnerBody>

      <Footer>
        <Inner>
          <AddressRow>
            <LeftColumn>
              <H4>Deliver to:</H4>
              <p>
                <BodyText>
                  <b>{orderData.venue.title}</b>
                </BodyText>
                {orderData.venue.address.line1 && (
                  <BodyText>{orderData.venue.address.line1}</BodyText>
                )}
                {orderData.venue.address.line2 && (
                  <BodyText>{orderData.venue.address.line2}</BodyText>
                )}
                {orderData.venue.address.city && (
                  <BodyText>{orderData.venue.address.city}</BodyText>
                )}
                {orderData.venue.address.postCode && (
                  <BodyText>{orderData.venue.address.postCode}</BodyText>
                )}
                {orderData.venue.phoneNumber && (
                  <BodyText>{orderData.venue.phoneNumber}</BodyText>
                )}
              </p>
            </LeftColumn>
          </AddressRow>
          <BodyText textAlign={'center'}>
            If you have any issues, please contact{' '}
            <a
              href='mailto:info@ordersmadesimple.com'
              style={{ color: 'white' }}
            >
              info@ordersmadesimple.com
            </a>
          </BodyText>
        </Inner>
      </Footer>
    </Wrapper>
  )
}

export default OrderApprovalScreen
